import { Signal } from '@preact/signals';
import { loadScript } from 'utils/loadScript';
import { toastMessages } from 'components/toastMessage/signals';
import { resolveLocality } from 'service/locality/resource';
import { COUNTRY_CZECH_ID } from 'utils/constants.ts';
import { localize } from 'i18n/localize';
import { tld } from 'utils/env';

const $t = localize();

// Docs https://gitlab.seznam.net/datacollect/geoproxy-static/-/wikis/home or https://gitlab.seznam.net/iva/documentation/-/blob/master/docs/components/GEO/home.md
const GEO_SCRIPT_URL = `https://geo.seznam.${tld()}/static/js/geo.js`;

async function initialize() {
	try {
		await loadScript(GEO_SCRIPT_URL);
	} catch (error) {
		console.error('Failed to load geo script', error);
	}
}

export async function getCurrentPosition(
	successCallback: PositionCallback,
	errorCallback: PositionErrorCallback,
	options: PositionOptions,
) {
	if (typeof window === 'undefined') {
		throw new Error('getCurrentPosition is only available in the browser');
	}

	if (!globalThis.window.geo) {
		await initialize();
	}
	const isEnabled = 'geolocation' in navigator;

	if (isEnabled && globalThis.window.geo) {
		return globalThis.window.geo.getCurrentPosition(successCallback, errorCallback, options);
	} else {
		return navigator.geolocation.getCurrentPosition(successCallback, errorCallback, options);
	}
}

export async function getCurrentPositionLocality(loadingGps: Signal<boolean>, preciseLocation = false) {
	loadingGps.value = true;
	try {
		const location = await new Promise<GeolocationPosition>((resolve, reject) => {
			getCurrentPosition((position) => resolve(position), reject, {
				timeout: 20_000,
				maximumAge: 3_600_000,
			});
		});
		let locality = await resolveLocality({
			gps_lat: location.coords.latitude,
			gps_lon: location.coords.longitude,
		});

		if (locality.countryId !== COUNTRY_CZECH_ID) {
			toastMessages.addMessage($t.filters.geo.abroadError, 'error');
			loadingGps.value = false;
			return null;
		}

		// Backend does not filter via address or street yet
		if (!preciseLocation) {
			if (locality.entityType === 'address' || locality.entityType === 'street') {
				if (locality.ward) {
					locality = {
						...locality,
						entityType: 'ward',
						entityId: locality.wardId,
					};
				} else if (locality.quarter) {
					locality = {
						...locality,
						entityType: 'quarter',
						entityId: locality.quarterId,
					};
				} else {
					locality = {
						...locality,
						entityType: 'municipality',
						entityId: locality.municipalityId,
					};
				}
			}
		}

		loadingGps.value = false;
		return locality;
	} catch (error) {
		if (error instanceof GeolocationPositionError) {
			if (error.code === GeolocationPositionError.TIMEOUT) {
				toastMessages.addMessage($t.filters.geo.timeoutError, 'error');
			} else {
				toastMessages.addMessage($t.filters.geo.disabledError, 'error');
			}
		} else {
			console.error('Loading gps location error', error);
			toastMessages.addMessage($t.filters.geo.otherError, 'error');
		}
		loadingGps.value = false;
		return null;
	}
}
